import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  EditorContent,
  useEditor,
  type Editor,
  type JSONContent,
} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Image } from "@tiptap/extension-image";
import { Placeholder } from "@tiptap/extension-placeholder";
import { TableKit } from "@tiptap/extension-table";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  ListTodo,
  Heading2,
  Quote,
  Link2,
  ImagePlus,
  Table2,
  Undo2,
  Redo2,
  Download,
  Trash2,
  Check,
  CloudUpload,
  AlertCircle,
  RotateCcw,
  PanelRightClose,
  PanelRightOpen,
  ExternalLink,
} from "lucide-react";
import { api, download } from "./api";
import { Modal, Spinner, dateLabel } from "./components";
import { useDocument } from "./useDocument";
import { Backlinks } from "./Backlinks";
import type {
  Folder,
  GameDocument,
  Asset,
  DocumentMeta,
  Entity,
  Backlink,
} from "./types";

export type EditorHandle = { flush: () => Promise<boolean> };
type Props = {
  initial: GameDocument;
  folders: Folder[];
  readOnly: boolean;
  onSaved: (doc: GameDocument) => void;
  onTrash: (doc: GameDocument) => void;
  onAsset: (asset: Asset) => void;
  documents: DocumentMeta[];
  entities: Entity[];
  onOpen: (link: Backlink) => void;
};
function textOf(n: JSONContent): string {
  return (
    n.text || n.content?.map(textOf).join(n.type === "doc" ? "\n\n" : "") || ""
  );
}
function markdown(n: JSONContent): string {
  if (n.type === "text") {
    let text = n.text || "";
    for (const mark of n.marks || []) {
      if (mark.type === "bold") text = `**${text}**`;
      if (mark.type === "italic") text = `*${text}*`;
      if (mark.type === "strike") text = `~~${text}~~`;
      if (mark.type === "code") text = "`" + text + "`";
      if (mark.type === "link") text = `[${text}](${mark.attrs?.href})`;
    }
    return text;
  }
  const children = n.content || [],
    inline = () => children.map(markdown).join("");
  switch (n.type) {
    case "doc":
      return children.map(markdown).join("\n\n");
    case "heading":
      return "#".repeat(n.attrs?.level || 2) + " " + inline();
    case "paragraph":
      return inline();
    case "bulletList":
      return children.map((c) => "- " + markdown(c)).join("\n");
    case "orderedList":
      return children.map((c, i) => `${i + 1}. ${markdown(c)}`).join("\n");
    case "taskList":
      return children
        .map((c) => `- [${c.attrs?.checked ? "x" : " "}] ${markdown(c)}`)
        .join("\n");
    case "listItem":
    case "taskItem":
      return children.map(markdown).join("\n");
    case "blockquote":
      return children.map((c) => "> " + markdown(c)).join("\n");
    case "codeBlock":
      return "```\n" + textOf(n) + "\n```";
    case "hardBreak":
      return "\n";
    case "horizontalRule":
      return "---";
    case "image":
      return `![${n.attrs?.alt || ""}](${window.location.origin}${n.attrs?.src})`;
    case "table": {
      const rows = children.map(
        (r) =>
          "| " +
          (r.content || [])
            .map((c) => textOf(c).replaceAll("|", "\\|"))
            .join(" | ") +
          " |",
      );
      if (rows.length)
        rows.splice(
          1,
          0,
          "| " +
            (children[0].content || []).map(() => "---").join(" | ") +
            " |",
        );
      return rows.join("\n");
    }
    default:
      return inline();
  }
}
function Toolbar({
  editor,
  upload,
  readOnly,
}: {
  editor: Editor | null;
  upload: () => void;
  readOnly: boolean;
}) {
  const items = [
    {
      label: "Жирный",
      icon: Bold,
      active: editor?.isActive("bold"),
      run: () => editor?.chain().focus().toggleBold().run(),
    },
    {
      label: "Курсив",
      icon: Italic,
      active: editor?.isActive("italic"),
      run: () => editor?.chain().focus().toggleItalic().run(),
    },
    {
      label: "Подчёркнутый",
      icon: Underline,
      active: editor?.isActive("underline"),
      run: () => editor?.chain().focus().toggleUnderline().run(),
    },
    {
      label: "Заголовок",
      icon: Heading2,
      active: editor?.isActive("heading"),
      run: () => editor?.chain().focus().toggleHeading({ level: 2 }).run(),
    },
    {
      label: "Маркированный список",
      icon: List,
      active: editor?.isActive("bulletList"),
      run: () => editor?.chain().focus().toggleBulletList().run(),
    },
    {
      label: "Нумерованный список",
      icon: ListOrdered,
      active: editor?.isActive("orderedList"),
      run: () => editor?.chain().focus().toggleOrderedList().run(),
    },
    {
      label: "Чек-лист",
      icon: ListTodo,
      active: editor?.isActive("taskList"),
      run: () => editor?.chain().focus().toggleTaskList().run(),
    },
    {
      label: "Цитата",
      icon: Quote,
      active: editor?.isActive("blockquote"),
      run: () => editor?.chain().focus().toggleBlockquote().run(),
    },
    {
      label: "Таблица",
      icon: Table2,
      run: () =>
        editor
          ?.chain()
          .focus()
          .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
          .run(),
    },
    { label: "Вставить изображение", icon: ImagePlus, run: upload },
  ];
  return (
    <div className="editor-toolbar" aria-label="Форматирование текста">
      {items.map(({ label, icon: Icon, active, run }) => (
        <button
          key={label}
          className="icon-button"
          aria-label={label}
          title={label}
          aria-pressed={!!active}
          onClick={run}
          disabled={readOnly}
        >
          <Icon size={17} />
        </button>
      ))}
      <span className="toolbar-divider" />
      <button
        className="icon-button"
        disabled={readOnly || !editor?.can().undo()}
        aria-label="Отменить"
        title="Отменить · Ctrl+Z"
        onClick={() => editor?.chain().focus().undo().run()}
      >
        <Undo2 size={17} />
      </button>
      <button
        className="icon-button"
        disabled={readOnly || !editor?.can().redo()}
        aria-label="Повторить"
        title="Повторить · Ctrl+Shift+Z"
        onClick={() => editor?.chain().focus().redo().run()}
      >
        <Redo2 size={17} />
      </button>
    </div>
  );
}
export const DocumentEditor = forwardRef<EditorHandle, Props>(
  function DocumentEditor(
    {
      initial,
      folders,
      readOnly,
      onSaved,
      onTrash,
      onAsset,
      documents,
      entities,
      onOpen,
    },
    ref,
  ) {
    const saving = useDocument(initial, onSaved),
      { doc, state, change, flush } = saving;
    const [inspector, setInspector] = useState(true),
      [linkModal, setLinkModal] = useState(false),
      [internalModal, setInternalModal] = useState(false),
      [internalTarget, setInternalTarget] = useState(""),
      [href, setHref] = useState(""),
      [uploading, setUploading] = useState(false),
      [uploadError, setUploadError] = useState(""),
      [confirmTrash, setConfirmTrash] = useState(false);
    const file = useRef<HTMLInputElement>(null),
      lastServerVersion = useRef(0);
    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: { levels: [1, 2, 3] },
          link: {
            openOnClick: false,
            HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
          },
        }),
        Image.configure({ allowBase64: false }),
        TableKit.configure({ table: { resizable: false } }),
        TaskList,
        TaskItem.configure({ nested: true }),
        Placeholder.configure({
          placeholder: "Начни с идеи. Что хочется проверить?",
        }),
      ],
      content: initial.content,
      editable: !readOnly,
      onUpdate: ({ editor }) => change({ content: editor.getJSON() }),
      editorProps: {
        handleClick: (_view, _pos, event) => {
          if (!event.ctrlKey && !event.metaKey) return false;
          const href = (event.target as Element)
            .closest("a")
            ?.getAttribute("href");
          if (!href?.startsWith("/?project=")) return false;
          const params = new URL(href, location.origin).searchParams;
          if (params.get("project") !== initial.projectId) return false;
          const id = params.get("document") || params.get("entity");
          if (!id) return false;
          event.preventDefault();
          onOpen({
            id,
            type: params.has("document") ? "document" : "entity",
            title: "Материал проекта",
          });
          return true;
        },
        attributes: {
          "aria-label": "Текст документа",
          role: "textbox",
          "aria-multiline": "true",
        },
      },
      shouldRerenderOnTransaction: true,
    });
    useImperativeHandle(ref, () => ({ flush }), [flush]);
    useEffect(() => {
      editor?.setEditable(!readOnly, false);
    }, [editor, readOnly]);
    useEffect(() => {
      if (
        lastServerVersion.current !== doc.version &&
        state === "saved" &&
        editor &&
        JSON.stringify(editor.getJSON()) !== JSON.stringify(doc.content)
      ) {
        editor.commands.setContent(doc.content, { emitUpdate: false });
      }
      lastServerVersion.current = doc.version;
    }, [doc, editor, state]);
    useEffect(() => {
      const save = (e: KeyboardEvent) => {
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
          e.preventDefault();
          void flush();
        }
      };
      window.addEventListener("keydown", save);
      return () => window.removeEventListener("keydown", save);
    }, [flush]);
    async function upload(image: File) {
      if (image.size > 20 * 1024 * 1024) {
        setUploadError("Изображение превышает 20 МБ.");
        return;
      }
      setUploading(true);
      setUploadError("");
      try {
        const data = new FormData();
        data.append("image", image);
        const asset = await api<Asset & { url: string }>(
          `/projects/${doc.projectId}/assets`,
          { method: "POST", body: data },
        );
        editor
          ?.chain()
          .focus()
          .setImage({ src: asset.url, alt: asset.name })
          .run();
        onAsset(asset);
      } catch (e) {
        setUploadError((e as Error).message);
      } finally {
        setUploading(false);
        if (file.current) file.current.value = "";
      }
    }
    const headings: string[] = [];
    function collect(n: JSONContent) {
      if (n.type === "heading") headings.push(textOf(n));
      n.content?.forEach(collect);
    }
    collect(doc.content);
    const outgoing = new Map<string, Backlink>();
    function links(n: JSONContent) {
      for (const mark of n.marks || []) {
        const href = mark.attrs?.href;
        if (mark.type !== "link" || !href?.startsWith("/?project=")) continue;
        const params = new URL(href, location.origin).searchParams;
        if (params.get("project") !== doc.projectId) continue;
        const id = params.get("document") || params.get("entity");
        if (!id) continue;
        const type = params.has("document") ? "document" : "entity",
          title =
            type === "document"
              ? documents.find((d) => d.id === id)?.title
              : entities.find((e) => e.id === id)?.title;
        outgoing.set(type + id, {
          type,
          id,
          title: title || "Материал в корзине",
        });
      }
      n.content?.forEach(links);
    }
    links(doc.content);
    const words = textOf(doc.content)
      .trim()
      .split(/\s+/)
      .filter(Boolean).length;
    const stateLabels = {
      saved: "Сохранено",
      dirty: "Есть изменения",
      saving: "Сохранение…",
      error: "Ошибка сохранения",
      conflict: "Конфликт версий",
    };
    return (
      <div
        className={`document-workspace ${inspector ? "" : "without-inspector"}`}
      >
        <div className="document-main">
          <div className="document-actionbar">
            <div className={`save-status ${state}`} role="status">
              {state === "saving" ? (
                <Spinner />
              ) : state === "saved" ? (
                <Check size={15} />
              ) : state === "dirty" ? (
                <CloudUpload size={16} />
              ) : (
                <AlertCircle size={16} />
              )}
              <span>{readOnly ? "Только просмотр" : stateLabels[state]}</span>
            </div>
            <div className="document-actions">
              <button
                className="text-button"
                onClick={() =>
                  download(
                    doc.title.replace(/[<>:"/\\|?*]/g, "_") + ".md",
                    "# " + doc.title + "\n\n" + markdown(doc.content),
                  )
                }
              >
                <Download size={15} />
                <span>Экспорт</span>
              </button>
              <button
                className="icon-button"
                aria-label="Удалить документ"
                title="В корзину"
                disabled={readOnly}
                onClick={() => setConfirmTrash(true)}
              >
                <Trash2 size={16} />
              </button>
              <button
                className="icon-button"
                aria-label={inspector ? "Скрыть свойства" : "Показать свойства"}
                title="Свойства документа"
                onClick={() => setInspector(!inspector)}
              >
                {inspector ? (
                  <PanelRightClose size={18} />
                ) : (
                  <PanelRightOpen size={18} />
                )}
              </button>
            </div>
          </div>
          {saving.message && (
            <div className="save-notice" role="alert">
              <span>{saving.message} Твой текст остаётся в редакторе.</span>
              {state === "error" && (
                <button onClick={() => void flush()}>
                  <RotateCcw size={14} />
                  Повторить
                </button>
              )}
              {saving.conflict && (
                <div className="conflict-actions">
                  <button
                    onClick={() =>
                      download(
                        doc.title + "-local.json",
                        JSON.stringify(doc, null, 2),
                        "application/json",
                      )
                    }
                  >
                    Скачать мою копию
                  </button>
                  <button onClick={() => saving.useServer()}>
                    Загрузить серверную
                  </button>
                  <button onClick={() => void saving.keepLocal()}>
                    Сохранить мою версию
                  </button>
                </div>
              )}
            </div>
          )}
          <Toolbar
            editor={editor}
            upload={() => file.current?.click()}
            readOnly={readOnly || uploading}
          />
          <div className="link-toolbar">
            <button
              className="text-button"
              disabled={readOnly}
              onClick={() => {
                setHref(editor?.getAttributes("link").href || "");
                setLinkModal(true);
              }}
            >
              <Link2 size={14} />
              Ссылка
            </button>
            <button
              className="text-button"
              disabled={readOnly}
              onClick={() => {
                setInternalTarget("");
                setInternalModal(true);
              }}
            >
              <Link2 size={14} />
              Материал проекта
            </button>
            {editor?.isActive("table") && (
              <>
                <button
                  className="text-button"
                  disabled={readOnly}
                  onClick={() => editor?.chain().focus().addRowAfter().run()}
                >
                  + Строка
                </button>
                <button
                  className="text-button"
                  disabled={readOnly}
                  onClick={() => editor?.chain().focus().addColumnAfter().run()}
                >
                  + Столбец
                </button>
                <button
                  className="text-button"
                  disabled={readOnly}
                  onClick={() => editor?.chain().focus().deleteTable().run()}
                >
                  Удалить таблицу
                </button>
              </>
            )}
            {uploading && (
              <span className="save-status">
                <Spinner />
                Загрузка изображения…
              </span>
            )}
          </div>
          {uploadError && (
            <div className="error-message" role="alert">
              {uploadError}
            </div>
          )}
          <input
            ref={file}
            className="file-input"
            type="file"
            accept="image/png,image/jpeg,image/webp"
            aria-label="Загрузить изображение"
            onChange={(e) => {
              if (e.target.files?.[0]) void upload(e.target.files[0]);
            }}
          />
          <article className="document-paper">
            <div className="document-kicker">
              ДОКУМЕНТ <span>/</span>{" "}
              {doc.template === "concept"
                ? "КОНЦЕПТ"
                : doc.template === "mechanic"
                  ? "МЕХАНИКА"
                  : doc.template === "playtest"
                    ? "ПЛЕЙТЕСТ"
                    : "ЗАМЕТКИ"}
            </div>
            <textarea
              className="document-title"
              aria-label="Название документа"
              rows={2}
              maxLength={160}
              value={doc.title}
              readOnly={readOnly}
              onChange={(e) => change({ title: e.target.value })}
            />
            <div className="document-byline">
              Создан {dateLabel(doc.createdAt)}
              <span>·</span>
              {words} слов
            </div>
            <EditorContent editor={editor} />
            <div className="paper-end">
              <span />
              Место для следующей идеи
              <span />
            </div>
          </article>
        </div>
        {inspector && (
          <aside className="document-inspector">
            <div className="inspector-heading">Свойства документа</div>
            <label>
              Папка
              <select
                aria-label="Папка документа"
                value={doc.folderId || ""}
                disabled={readOnly}
                onChange={(e) => change({ folderId: e.target.value || null })}
              >
                <option value="">Без папки</option>
                {folders.map((f) => (
                  <option value={f.id} key={f.id}>
                    {f.title}
                  </option>
                ))}
              </select>
            </label>
            <div className="inspector-group">
              <span className="eyebrow">СВЯЗАННЫЕ МАТЕРИАЛЫ</span>
              {outgoing.size ? (
                <div className="backlinks">
                  {[...outgoing.values()].map((link) => (
                    <button
                      key={link.type + link.id}
                      onClick={() => onOpen(link)}
                    >
                      <span>{link.title}</span>
                      <ExternalLink size={12} />
                    </button>
                  ))}
                </div>
              ) : (
                <p className="small-text muted">
                  Вставь ссылку кнопкой «Материал проекта».
                </p>
              )}
            </div>
            <Backlinks
              projectId={doc.projectId}
              type="document"
              id={doc.id}
              version={doc.version}
              onOpen={onOpen}
            />
            <div className="inspector-group">
              <span className="eyebrow">СТРУКТУРА</span>
              {headings.length ? (
                <nav aria-label="Структура документа">
                  {headings.map((h, i) => (
                    <button
                      key={i}
                      className="outline-link"
                      onClick={() => {
                        const el =
                          editor?.view.dom.querySelectorAll("h1,h2,h3")[i];
                        el?.scrollIntoView({
                          behavior: "smooth",
                          block: "center",
                        });
                      }}
                    >
                      {h || "Без названия"}
                    </button>
                  ))}
                </nav>
              ) : (
                <p className="muted small-text">
                  Добавь заголовки — здесь появится структура документа.
                </p>
              )}
            </div>
            <div className="inspector-group">
              <span className="eyebrow">О ДОКУМЕНТЕ</span>
              <dl>
                <div>
                  <dt>Версия</dt>
                  <dd>{doc.version}</dd>
                </div>
                <div>
                  <dt>Изменён</dt>
                  <dd>{dateLabel(doc.updatedAt)}</dd>
                </div>
                <div>
                  <dt>Объём</dt>
                  <dd>{words} слов</dd>
                </div>
              </dl>
            </div>
            <div className="inspector-tip">
              <CloudUpload size={20} />
              <p>Изменения сохраняются автоматически после паузы в наборе.</p>
              <kbd>Ctrl + S</kbd>
              <span>сохранить сейчас</span>
            </div>
          </aside>
        )}
        {internalModal && (
          <Modal
            title="Ссылка на материал проекта"
            close={() => setInternalModal(false)}
          >
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const [type, id] = internalTarget.split(":");
                if (!id || !editor) return;
                const title =
                  type === "document"
                    ? documents.find((d) => d.id === id)?.title
                    : entities.find((e) => e.id === id)?.title;
                const href = `/?project=${doc.projectId}&${type === "entity" ? "view=entities&" : ""}${type}=${id}`;
                if (editor.state.selection.empty)
                  editor
                    .chain()
                    .focus()
                    .insertContent({
                      type: "text",
                      text: title || "Материал проекта",
                      marks: [{ type: "link", attrs: { href } }],
                    })
                    .run();
                else editor.chain().focus().setLink({ href }).run();
                setInternalModal(false);
              }}
            >
              <label>
                Материал
                <select
                  aria-label="Связанный материал"
                  required
                  value={internalTarget}
                  onChange={(e) => setInternalTarget(e.target.value)}
                >
                  <option value="">Выбери материал</option>
                  <optgroup label="Документы">
                    {documents
                      .filter((d) => d.id !== doc.id)
                      .map((d) => (
                        <option key={d.id} value={"document:" + d.id}>
                          {d.title}
                        </option>
                      ))}
                  </optgroup>
                  <optgroup label="Сущности">
                    {entities.map((entity) => (
                      <option key={entity.id} value={"entity:" + entity.id}>
                        {entity.title}
                      </option>
                    ))}
                  </optgroup>
                </select>
              </label>
              <p className="muted small-text">
                Открой связь из панели справа или нажатием Ctrl + щелчок в
                тексте.
              </p>
              <div className="modal-actions">
                <button
                  type="button"
                  className="secondary"
                  onClick={() => setInternalModal(false)}
                >
                  Отмена
                </button>
                <button className="primary" disabled={!internalTarget}>
                  Вставить ссылку
                </button>
              </div>
            </form>
          </Modal>
        )}
        {linkModal && (
          <Modal title="Вставить ссылку" close={() => setLinkModal(false)}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (
                  href &&
                  !/^(https?:\/\/|mailto:|\/\?project=)/i.test(href)
                ) {
                  setUploadError(
                    "Используй https://, http://, mailto: или внутреннюю ссылку проекта.",
                  );
                  return;
                }
                if (href)
                  editor
                    ?.chain()
                    .focus()
                    .extendMarkRange("link")
                    .setLink({ href })
                    .run();
                else editor?.chain().focus().unsetLink().run();
                setLinkModal(false);
              }}
            >
              <label>
                Адрес ссылки
                <input
                  autoFocus
                  value={href}
                  onChange={(e) => setHref(e.target.value)}
                  placeholder="https://…"
                />
              </label>
              <p className="muted small-text">
                Выдели текст перед добавлением ссылки. Пустой адрес уберёт
                ссылку.
              </p>
              <div className="modal-actions">
                <button
                  type="button"
                  className="secondary"
                  onClick={() => setLinkModal(false)}
                >
                  Отмена
                </button>
                <button className="primary">
                  <ExternalLink size={15} />
                  Применить
                </button>
              </div>
            </form>
          </Modal>
        )}
        {confirmTrash && (
          <Modal
            title="Переместить документ в корзину?"
            close={() => setConfirmTrash(false)}
          >
            <p>«{doc.title}» можно будет восстановить вместе с содержимым.</p>
            <div className="modal-actions">
              <button
                className="secondary"
                onClick={() => setConfirmTrash(false)}
              >
                Отмена
              </button>
              <button
                className="danger"
                onClick={async () => {
                  if (await flush()) {
                    setConfirmTrash(false);
                    onTrash(saving.getCurrent());
                  }
                }}
              >
                В корзину
              </button>
            </div>
          </Modal>
        )}
      </div>
    );
  },
);
